import { Link } from "react-router-dom";
import { ArrowRight, Heart, Eye, Compass, Users, Award, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import { COMPANY } from "@/data/services";
import { SITE_URL } from "@/config/site";

export default function Apropos() {
  return (
    <>
      <SEO
        title="À propos — Perspective Assurance, votre courtier à Lyon"
        description="Découvrez Perspective Assurance, cabinet de courtage à Lyon. Notre mission : un conseil indépendant, humain et personnalisé pour particuliers et professionnels."
        canonical={`${SITE_URL}/a-propos`}
      />

      <section className="bg-cream py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 relative">
          <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-4">À propos</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-forest leading-[1.05] tracking-tight max-w-3xl text-balance">
            Le courtage tel qu'il devrait <span className="italic text-terracotta">toujours</span> être.
          </h1>
          <p className="mt-8 text-xl text-forest/70 leading-relaxed max-w-2xl">
            Perspective Assurance est né d'un constat simple : trop de clients se sentent perdus face à des contrats opaques. Notre métier : remettre l'humain et la clarté au cœur de l'assurance.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <img
            src="https://images.pexels.com/photos/5816300/pexels-photo-5816300.jpeg"
            alt="Julien Maertens — Fondateur de Perspective Assurance"
            className="rounded-3xl shadow-xl w-full aspect-square object-cover"
            loading="lazy"
          />
          <div>
            <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-4">Notre fondateur</div>
            <h2 className="font-display text-3xl sm:text-4xl text-forest leading-tight mb-6">
              Julien Maertens, votre interlocuteur unique.
            </h2>
            <div className="space-y-4 text-forest/75 leading-relaxed">
              <p>Spécialiste du courtage depuis plus d'une décennie, Julien a fait le choix de l'indépendance pour ne défendre qu'un seul intérêt : le vôtre.</p>
              <p>De la première analyse de vos besoins jusqu'à la gestion d'un éventuel sinistre, il vous accompagne personnellement, en métropole comme dans les DOM-TOM.</p>
              <p>Sa promesse : un appel, une réponse claire, des solutions concrètes — sans jargon, sans intermédiaire.</p>
            </div>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-terracotta font-outfit font-medium hover:gap-3 transition-all">
              Prendre rendez-vous <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-cream">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-12">
          <h2 className="font-display text-3xl sm:text-4xl text-forest leading-tight mb-14 max-w-2xl">Nos valeurs.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Heart, title: "Humain", text: "Pas de standard automatique. Vous parlez à une personne qui connaît votre dossier." },
              { icon: Eye, title: "Transparent", text: "Tous les contrats, exclusions et tarifs sont expliqués sans détour." },
              { icon: Compass, title: "Indépendant", text: "Nous comparons sans biais une vingtaine de compagnies pour vous trouver le bon contrat." },
              { icon: Users, title: "Proximité", text: "Basés à Lyon 6e, mais présents pour vous où que vous soyez en France." }
            ].map((v, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-soft" data-testid={`value-${i}`}>
                <div className="w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center mb-4">
                  <v.icon className="w-5 h-5 text-terracotta" />
                </div>
                <h3 className="font-outfit font-semibold text-forest mb-2">{v.title}</h3>
                <p className="text-sm text-forest/70 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-forest text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-12 text-center">
          <Award className="w-12 h-12 text-terracotta mx-auto mb-6" />
          <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-6">Un mot à nous dire ?</h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">Le meilleur moyen de nous découvrir, c'est encore d'échanger. Sans engagement, sans pression commerciale.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" data-testid="about-cta-contact" className="inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-[#1E3F73] text-white rounded-full px-8 py-4 font-outfit font-medium">
              Nous contacter <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${COMPANY.phone}`} data-testid="about-cta-call" className="inline-flex items-center justify-center gap-2 border border-white/30 text-white hover:border-white rounded-full px-8 py-4 font-outfit font-medium">
              <Phone className="w-4 h-4" /> {COMPANY.phoneDisplay}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
